import Axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import {
	DependencyService,
	DialogService,
	IBaseResponse,
	TCreditEntryMethod,
	ILoginPinRequestResponse,
	ILoginPinVerifyResponse,
	PushService,
	SoundService
} from '../services';
import {
	FeatureFlag,
	IAuthConfig,
	Permission,
	SessionStore,
	SimpleMenuStore,
	SpecialsStore,
	CustomerProfileStore,
	ICustomerProfile,
	OrderStore,
	LocationStore,
	ItemFavoriteStore,
	RecentItemStore,
	LineItemStatus,
	TLineItemStatus,
	VehiclesStore,
	ITokenizeResult,
	TicketsStore,
	BRAND,
	MenuStore, TICKET_STATUS,
	ContainerTransitionMappingStore,
	HistoryStore,
} from '../store';
import {Platform, Dialog, Loading, Notify} from 'quasar';
import dayjs from 'dayjs';
import {v4} from 'uuid';
import {SplashScreen} from '@capacitor/splash-screen';
import {Device} from '@capacitor/device';
import {App} from '@capacitor/app';
import {ERouteName} from '../router';

// Used in Payment Requests
export interface PaymentMethodData {
	data?: any;
	supportedMethods: string;
}

class Web {
	// This will default to the value in quasar.config.ts
	// but you can overwrite it by adding a .env file to the root
	// of this repo with the following line:
	// DEV_API_HOST=https://YOUR_C_HERE.tp-devel.com/api/v2
	// We're removing /api/v2 to be backward compatible. It is no
	// longer necessary to include /api/v2 in our DEV_API_HOST initialization.
	// We need to remove the trailing slash because for some reason when the C tunnels to the LC
	// it causes a 400 if there are duplicate slashes in the URL. This was added because of Trevor.
	DEV_HOST = (process.env.DEV_API_HOST as string).replace('/api/v2', '').replace(/\/$/, '');
	DEV_DIRECT_HOST = 'https://c1.tp-devel.com';
	QA_HOST = 'https://tcdn.tp-qa.com';
	QA_DIRECT_HOST = 'https://c1.tp-qa.com';
	PROD_HOST = 'https://tcdn.tchpt.com';
	PROD_DIRECT_HOST = 'https://c1.tchpt.com';

	started = false;
	unauthenticatedClient!: AxiosInstance;
	baseStarted!: Promise<void>;

	authenticatedClient!: AxiosInstance;
	loggedInStarted!: Promise<void>;

	orderingClients: Record<string, AxiosInstance> = {};
	trackingClient: AxiosInstance | null = null;
	baseClient: AxiosInstance | null = null;

	appId: string | null = null;

	deviceId: string | null = null;
	sessionId: string | null = null;


	wssUrl: string | null = null;
	websocket: WebSocket | null = null;
	ignoreDirtyClose = false;

	// WS Heartbeat
	websocketHeartbeatInterval = 10000;
	websocketHeartbeatTimeout: number | null = null;
	websocketDownInterval = 7000;
	websocketDownTimeout: number | null = null;

	websocketReconnectCount = 0;
	maxWebsocketReconnects = 5;

	screenPopsHandled = false;

	private baseStartedResolve!: () => void;
	private loggedInStartedResolve!: () => void;


	constructor() {
		this.setupBaseStartedPromise();
		this.setupLoggedInStartedPromise();
	}

	async getAppId() {
		if(!this.appId) {
			const result = await App.getInfo();
			this.appId = result.id;
		}
		return this.appId;
	}

	getAppVersion() {
		return process.env.VUE_APP_VERSION || '';
	}

	async getDeviceId() {
		if(!this.deviceId) {
			const info = await Device.getId();
			this.deviceId = info.uuid;
		}
		return this.deviceId;
	}

	async restart() {
		console.log('Restarting Webservices');
		this.setupBaseStartedPromise();
		this.setupLoggedInStartedPromise();
		return this.start();
	}

	// This is used to reload all default calls, not a full restart
	async reload() {
		const promises = [];
		promises.push(SessionStore.fetchBrandConfig());
		if(SessionStore.authConfig) {
			promises.push(CustomerProfileStore.fetchProfile());
			promises.push(SimpleMenuStore.fetchMenu());
			promises.push(SpecialsStore.fetchSpecials());
			promises.push(LocationStore.fetchLocations());
			promises.push(ItemFavoriteStore.fetchFavorites());
			// Right now only 7Brew supports shared favorites
			// This check can be removed once the 7Brew branch
			// is merged into devel and deployed
			if(SessionStore.isBrand(BRAND.SEVEN_BREW)) {
				promises.push(ItemFavoriteStore.fetchSharedFavorites());
			}
			promises.push(RecentItemStore.fetchRecentItems());
		}
		return Promise.all(promises);
	}

	get defaultHost() {
		// Special override for if we have configured a different default host
		if(process.env.VUE_APP_DEFAULT_ENVIRONMENT) {
			if(process.env.VUE_APP_DEFAULT_ENVIRONMENT === 'development') {
				return this.DEV_HOST;
			} else if(process.env.VUE_APP_DEFAULT_ENVIRONMENT === 'qa') {
				return this.QA_HOST;
			} else if(process.env.VUE_APP_DEFAULT_ENVIRONMENT === 'production') {
				// Only user prod if specified, we will fall back to prod below if needed
				return this.PROD_HOST;
			}
		}
		if((location.host.indexOf('localhost') !== -1 || location.host.indexOf('tp-devel.com') !== -1) && !Platform.is.capacitor) {
			return this.DEV_HOST;
		} else if(location.host.indexOf('tp-qa.com') !== -1) {
			return this.QA_HOST;
		} else {
			return this.PROD_HOST;
		}
	}

	get host() {
		if(!SessionStore.selectedEnvironment || SessionStore.selectedEnvironment === 'default') {
			return this.defaultHost;
		} else if(SessionStore.selectedEnvironment === 'development') {
			return this.DEV_HOST;
		} else if(SessionStore.selectedEnvironment === 'development_direct') {
			return this.DEV_DIRECT_HOST;
		} else if(SessionStore.selectedEnvironment === 'qa') {
			return this.QA_HOST;
		} else if(SessionStore.selectedEnvironment === 'qa_direct') {
			return this.QA_DIRECT_HOST;
		} else if(SessionStore.selectedEnvironment === 'production_direct') {
			return this.PROD_DIRECT_HOST;
		} else {
			// Always fall back to production
			return this.PROD_HOST;
		}
	}

	async start() {
		this.unauthenticatedClient = Axios.create({
			baseURL: `${this.host}/api/v2/customer`,
			timeout: 30000,
			// headers: []
		});

		this.unauthenticatedClient.interceptors.request.use(this.addHeadersInterceptor.bind(this));

		this.unauthenticatedClient.interceptors.response.use(this.errorCheckingInterceptor.bind(this), (error) => {
			// Do Nothing
			return Promise.reject(error);
		});

		this.baseStartedResolve();

		await Promise.all([
			SessionStore.fetchBrandConfig()
		]);
		this.started = true;
	}

	async addHeadersInterceptor(request: AxiosRequestConfig) {
		request.headers.TP_DOMAIN = await this.getDomain();
		request.headers.TP_DEVICE_TIMESTAMP = dayjs().unix();
		request.headers.TP_DEVICE_ID = await this.getDeviceId();
		if(!this.sessionId) {
			this.sessionId = v4();
		}
		request.headers.TP_SESSION_ID = this.sessionId;
		request.headers.TP_APP_VERSION = process.env.VUE_APP_VERSION as string;
		if(!request.params) {
			request.params = {};
		}
		request.params.__tpcb = Date.now();

		return request;
	}

	errorCheckingInterceptor(response: AxiosResponse) {
		// Check if we are logged out
		if(response.data.status !== 200) {
			// This will happen when the lc returns a non-200
			// We need to handle the error object structure passed back
			const errorMessage = this.errorMessageFromResponse(response);
			return Promise.reject(new Error(errorMessage));
		}
		return response;
	}

	errorHandlerInterceptor(error: any) {
		if(error.isAxiosError && error.response && error.response.data && error.response.data.errors) {
			if(error.response.data.errors[0].code === 'CODE_INVALID_API_KEY') {
				this.logout(true).then();
			}
		}
		return Promise.reject(error);
	}

	// Right now this just handled web but in the future it will handle mobile
	async getDomain() {
		if(process.env.FORCE_DOMAIN) {
			return process.env.FORCE_DOMAIN;
		}
		if(Platform.is.capacitor) {
			const appId = await this.getAppId();
			if(appId) {
				return appId;
			}
		}
		return `${location.protocol}//${location.host}`;
	}

	setupBaseStartedPromise() {
		this.baseStarted = new Promise<void>(resolve => {
			this.baseStartedResolve = resolve;
		});
	}

	setupLoggedInStartedPromise() {
		this.loggedInStarted = new Promise<void>(resolve => {
			this.loggedInStartedResolve = resolve;
		});
	}

	async pinRequest(phone: string, voice = false) {
		await this.baseStarted;
		const response = await this.unauthenticatedClient.post<IBaseResponse<ILoginPinRequestResponse>>('login/pin_request', {
			phone: phone,
			voice: voice,
			domain: await this.getDomain()
		});
		return response.data.data;
	}

	async pinVerify(phone: string, pin: string): Promise<ILoginPinVerifyResponse> {
		await this.baseStarted;
		const response = await this.unauthenticatedClient.post<IBaseResponse<ILoginPinVerifyResponse>>('login/pin_verify', {
			phone: phone,
			pin: pin,
			domain: await this.getDomain()
		});
		return response.data.data;
	}

	getLoggedInAxios(authConfig: IAuthConfig) {
		return Axios.create({
			baseURL: `${this.host}/api/v2/customer/${authConfig.userBase}/brand/${authConfig.franchisorBase}`,
			timeout: 30000,
			headers: {
				API_KEY: authConfig.apiKey
			}
		});
	}

	async testAuthConfig(authConfig: IAuthConfig) {
		const authAxios = this.getLoggedInAxios(authConfig);
		authAxios.interceptors.request.use(this.addHeadersInterceptor.bind(this));
		// We don't want to attach the error handler to this request
		authAxios.interceptors.response.use(this.errorCheckingInterceptor.bind(this));

		try {
			await authAxios.get<IBaseResponse<{profile: ICustomerProfile}>>('profile');
			return true;
		} catch(error) {
			return false;
		}
	}

	async startLoggedIn(authConfig: IAuthConfig) {
		this.authenticatedClient = this.getLoggedInAxios(authConfig);

		this.authenticatedClient.interceptors.request.use(this.addHeadersInterceptor.bind(this));

		this.authenticatedClient.interceptors.response.use(this.errorCheckingInterceptor.bind(this), this.errorHandlerInterceptor.bind(this));


		this.loggedInStartedResolve();
		try {
			const promises: Promise<void>[] = [
				CustomerProfileStore.fetchProfile(),
				SimpleMenuStore.fetchMenu(),
				SpecialsStore.fetchSpecials(),
				LocationStore.fetchLocations(),
				ItemFavoriteStore.fetchFavorites(),
				RecentItemStore.fetchRecentItems(),
				// Un-commend and remove below once this is deployed to prod
				// ContainerTransitionMappingStore.fetchContainerTransitionMappings(),
				// TicketsStore.fetchLatestTicket().catch(
				// 	(error) => {
				// 		console.warn('Failed to fetch latest ticket', error);
				// 		TicketsStore.clearLatestTicket();
				// 	}
				// )
			];

			// Right now only 7Brew supports shared favorites
			// This check can be removed once the 7Brew branch
			// is merged into devel and deployed
			if(SessionStore.isBrand(BRAND.SEVEN_BREW)) {
				promises.push(ItemFavoriteStore.fetchSharedFavorites());
				// Same deal for container mapping/last ticket, we don't have the endpoints
				// deployed yet in prod
				promises.push(ContainerTransitionMappingStore.fetchContainerTransitionMappings());
				promises.push(TicketsStore.fetchLatestTicket().catch(
					(error) => {
						console.warn('Failed to fetch latest ticket', error);
						TicketsStore.clearLatestTicket();
					}
				));
			}

			await Promise.all(promises);
		} catch(error) {
			// Either the server is down or the API key is invalid
			console.log(error);
			await this.logout(true);
		}
	}

	getTrackingClient(
		franchisorBase: string,
		customerBase: string,
		trackUrl: string,
		trackCode: string
	) {
		if(!this.trackingClient || this.trackingClient.defaults.baseURL !== `${trackUrl}/track`) {
			this.trackingClient = Axios.create({
				baseURL: `${trackUrl}/track`,
				timeout: 30000,
			});

			this.trackingClient.defaults.params = {
				franchisorBase: franchisorBase,
				customerBase: customerBase,
			};

			this.trackingClient.interceptors.request.use(this.addHeadersInterceptor.bind(this));
		}
		// Always update the track code
		this.trackingClient.defaults.headers.TRACK_CODE = trackCode;

		return this.trackingClient;
	}

	getOrderingClient(locationId: string | null = null) {
		if(!locationId) {
			locationId = OrderStore.selectedLocationId;
			if(!locationId) {
				throw new Error('No location id');
			}
		}
		if(!(locationId in this.orderingClients)) {
			const authConfig = SessionStore.authConfig;
			if(!authConfig) {
				throw new Error('No auth config');
			}

			this.orderingClients[locationId] = Axios.create({
				baseURL: `${this.host}/api/v2/location/${locationId}`,
				timeout: 30000,
				headers: {
					API_KEY: authConfig.apiKey,
					CUSTOMER_BASE: authConfig.userBase,
				}
			});

			this.orderingClients[locationId].interceptors.request.use(this.addHeadersInterceptor.bind(this));

			this.orderingClients[locationId].interceptors.response.use(this.errorCheckingInterceptor.bind(this), this.errorHandlerInterceptor.bind(this));
		}
		return this.orderingClients[locationId];
	}

	async claimBrandReward(rewardName: string) {
		await this.loggedInStarted;
		await this.authenticatedClient.post<IBaseResponse<{message: string}>>(`reward/${rewardName}/claim`);
		// Reload profile after claiming
		await CustomerProfileStore.fetchProfile();
	}

	async claimOpportunity(opportunityId: string) {
		await this.loggedInStarted;
		await this.authenticatedClient.post<IBaseResponse<{message: string}>>(`opportunities/${opportunityId}/claim`);
		// Reload profile after claiming
		await CustomerProfileStore.fetchProfile();
	}

	async submitNPSSurvey(opportunityId: string, rating: number, responseText: string) {
		await this.loggedInStarted;
		await this.authenticatedClient.post<IBaseResponse<{message: string}>>(`nps/${opportunityId}/submit`, {
			rating: rating,
			responseText: responseText
		});
		// Reload profile after claiming
		await CustomerProfileStore.fetchProfile();
	}

	async base64Image(url: string) {
		const response = await Axios.get(url, {
			responseType: 'arraybuffer'
		});
		return 'data:image/png;base64,' + Buffer.from(response.data, 'binary').toString('base64');
	}

	async getPaymentSession(locationId: string, paymentChannel: TCreditEntryMethod, url: string) {
		await this.loggedInStarted;
		const response = await this.authenticatedClient.post<IBaseResponse<{session: Record<string, any>}>>(`locations/${locationId}/payments/session`, {
			paymentChannel: paymentChannel,
			url: url,
		});
		return response.data.data.session;
	}

	async tokenize(locationId: string, paymentChannel: TCreditEntryMethod, payload: Record<string, any>) {
		await this.loggedInStarted;
		const response = await this.authenticatedClient.post<IBaseResponse<{signedToken: string}>>(`locations/${locationId}/payments/tokenize`, {
			paymentChannel: paymentChannel,
			payload: payload,
		});
		return response.data.data.signedToken;
	}

	async reloadGiftCard(tokenizeResult: ITokenizeResult, amount: number) {
		await this.loggedInStarted;
		const response = await this.authenticatedClient.post<IBaseResponse<{message: string}>>('gift_card/reload', {
			signedToken: tokenizeResult.signedToken,
			accountId: tokenizeResult.accountId,
			cvv: tokenizeResult.cvv,
			amount: amount
		});
		return response.data.data.message;
	}

	async getPaymentRequestConfig(locationId: string, paymentChannel: TCreditEntryMethod) {
		await this.loggedInStarted;
		const response = await this.authenticatedClient.get<IBaseResponse<{config: PaymentMethodData}>>(`locations/${locationId}/payments/payment_request_config`, {
			params: {
				paymentChannel: paymentChannel,
			}
		});
		return response.data.data.config;
	}

	async deleteAccount() {
		await this.authenticatedClient.post<IBaseResponse<{message: string}>>('delete');
		// We aren't calling logout here because we want to show a message on the screen before we redirect
	}

	async logout(skipServer = false) {
		this.screenPopsHandled = false;
		// First we need to tell the server, if that fails we can't do anything else
		if(!skipServer) {
			try {
				Loading.show();
				await this.authenticatedClient.post('logout');
			} catch(error) {
				Loading.hide();
				console.error(error);
				Dialog.create({
					title: 'Logout failed',
					message: 'We were unable to log you out. Please try again later.',
				});
				return;
			}
		}

		// Clear Session store, leave brand config
		SessionStore.clearAuthConfig();
		CustomerProfileStore.clearCustomerProfile();
		VehiclesStore.clearCustomerVehicles();
		SessionStore.setSelectedBackground(0);
		SessionStore.clearAllRequested();

		// Clear Order Store
		OrderStore.clearAll();

		// Clear Menu store
		SimpleMenuStore.clearMenu();
		MenuStore.clearMenu();

		// Clear Ticket store
		TicketsStore.clearTickets();

		// Clear RecentItem store
		RecentItemStore.clearRecentItems();

		// Clear FavoriteItem store
		ItemFavoriteStore.clearFavorites();

		// Clear Location store
		LocationStore.clearLocations();

		// Clear History store
		HistoryStore.clearHistory();

		// Clear clients
		this.orderingClients = {};
		this.trackingClient = null;

		// Clear Specials
		SpecialsStore.clearSpecials();
		Loading.hide();
	}

	processOpenUrl(url: string) {
		SessionStore.setInitialPath(null);
		const urlParts = this.getUrlFromPath(url);
		const result = DependencyService.getRouter().resolve(urlParts.pathname);
		if(result && result.route.name !== '404') {
			const query: Record<string, string> = {};
			for(const [key, value] of urlParts.searchParams.entries()) {
				query[key] = value;
			}
			DependencyService.getRouter().push({
				path: urlParts.pathname,
				query: query
			});
		} else {
			DependencyService.getRouter().push({
				path: '/home'
			});
		}
	}

	getCurrentPath() {
		if(process.env.NODE_ENV === 'development' && location.hash !== '') {
			// Don't need to add search here, it's in the hash
			return location.hash.substring(1);
		} else {
			return location.pathname + (location.search ? location.search : '');
		}
	}

	getUrlFromPath(path: string) {
		let url: URL;
		// Sometimes this a full url, sometimes just a path
		try {
			url = new URL(path);
		} catch(error) {
			url = new URL(`${location.protocol}//${location.host}${path}`);
		}
		return url;
	}

	async handleScreenPops(force = false) {
		if(this.screenPopsHandled && !force) {
			return;
		}
		this.screenPopsHandled = true;

		if(CustomerProfileStore.customerProfile && SessionStore.brandConfig) {
			const respectInitialPaths = [
				'/opportunity/',
				'/nps/',
				'/mobile/'
			];
			// Some urls we want to try BEFORE we look for things like opportunities or nps
			const respectInitialPathFirst = respectInitialPaths.find((path) => {
				return SessionStore.initialPath && SessionStore.initialPath.startsWith(path);
			});

			// We are only using the app-install-reward, opportunity, and nps screens for generic right now
			// because 7Brew doesn't support those paths yet
			if(!CustomerProfileStore.customerProfile.acceptedTOS) {
				if(DependencyService.getRouter().currentRoute.name !== ERouteName.AcceptTOS) {
					await DependencyService.getRouter().push({
						name: ERouteName.AcceptTOS,
					});
				}
			} else if(SessionStore.isBrand(BRAND.GENERIC) && Platform.is.capacitor && SessionStore.brandConfig.loyaltyAppInstallRewardOn && !CustomerProfileStore.customerProfile.receivedAppInstallReward) {
				await DependencyService.getRouter().push('/app-install-reward');
			} else if(respectInitialPathFirst && SessionStore.initialPath && !SessionStore.initialPath.startsWith('/login')) {
				this.processOpenUrl(SessionStore.initialPath);
			} else if(SessionStore.isBrand(BRAND.GENERIC) && CustomerProfileStore.hasUnclaimedOpportunities) {
				await DependencyService.getRouter().push(`/opportunity/${CustomerProfileStore.unclaimedOpportunityId}`);
			} else if(CustomerProfileStore.hasUnclaimedNPSOpportunities) {
				await DependencyService.getRouter().push(`/nps/${CustomerProfileStore.unclaimedNPSOpportunityId}`);
			} else if(SessionStore.initialPath && !SessionStore.initialPath.startsWith('/login')) {
				this.processOpenUrl(SessionStore.initialPath);
			} else {
				await DependencyService.getRouter().push({name: 'Home'}); // TODO: use ERouteName
			}
			await SplashScreen.hide();

			// Here is where we can put all our "modal pops", we will not support more than 1 pop for now
			if(Platform.is.capacitor && SessionStore.isEnabled(FeatureFlag.PUSH_NOTIFICATIONS) && CustomerProfileStore.customerProfile && !SessionStore.hasBeenRequested(Permission.PUSH_NOTIFICATIONS)) {
				await PushService.enablePushNotifications();
			}
		}
	}

	showError(
		error: any | null,
		errorMessage: string = 'An unknown error occurred, please try again or contact staff if the problem persists',
		extraProps: Record<string, any> = {}
	) {
		if(error && error.isAxiosError && !error?.response) {
			// no response (lost connection, timeout, etc.)
			// TODO: distinguish between timeout, lost connection, cross-domain, etc., by parsing message or code
			console.warn('connection error', error.toJSON());
			return DialogService.getNetworkErrorDialog();
		}

		errorMessage = this.showErrorCommon(error, errorMessage);
		return DialogService.getErrorDialog({
			message: errorMessage,
			...extraProps
		});
	}

	showErrorToast(
		error: any | null,
		errorMessage: string = 'An unknown error occurred, please try again or contact staff if the problem persists',
		extraProps: Record<string, any> = {}
	) {
		errorMessage = this.showErrorCommon(error, errorMessage);
		return Notify.create({
			message: errorMessage,
			...extraProps
		});
	}

	showErrorCommon(
		error: any | null,
		errorMessage: string = 'An unknown error occurred, please try again or contact staff if the problem persists'
	) {
		console.error(error);
		if(error && error.isAxiosError && error.response && error.response.data && error.response.data.errors) { // Handles axios errors from non-2XX responses
			const response = error.response as AxiosResponse;
			errorMessage = this.errorMessageFromResponse(response, errorMessage);
		} else if(error && error.message) { // handles 2XX responses from server but returns status of non-zero
			errorMessage = (error as Error).message;
		}
		return errorMessage;
	}

	errorMessageFromResponse(response: AxiosResponse, defaultMessage = 'An unknown error occurred, please try again or contact staff if the problem persists') {
		const errorMessage = response.data.errors;
		if(Array.isArray(errorMessage)) {
			return errorMessage.map((err) => {
				if(err === null) {
					return 'Unknown error';
				} else if(typeof err === 'string') {
					return err;
				} else if('detail' in err) {
					return err.detail as string;
				}
				return JSON.stringify(err);
			}).join('\n');
		} else if(typeof errorMessage === 'string') {
			return errorMessage;
		} else {
			return defaultMessage;
		}
	}

	assetPath(path: string | null | undefined): string | null {
		if(!path) {
			return null;
		}
		// ignore full paths or paths to app-bundled images
		if(path.startsWith('http') || this.isBundledImage(path)) {
			return path;
		}
		if(path.indexOf('/') === 0) {
			return `${this.host}${path}`;
		}
		return `${this.host}/${path}`;
	}

	stopWebsockets() {
		if(this.websocketHeartbeatTimeout !== null) {
			clearTimeout(this.websocketHeartbeatTimeout);
		}
		if(this.websocket) {
			this.websocket.close();
		}
		this.websocket = null;
		this.wssUrl = null;
	}


	startWebsockets(url: string | null = null) {
		if(url) {
			this.wssUrl = url;
		}
		if(this.wssUrl === null) {
			console.error('Missing WS URL');
			return;
		}
		if(this.websocket) {
			console.warn('Websocket already started');
			return;
		}
		return new Promise((resolve, reject) => {
			if(this.websocketReconnectCount > this.maxWebsocketReconnects) {
				this.stopWebsockets();
				reject(new Error('Too many websocket reconnect attempts'));
				return;
			}
			this.websocketReconnectCount++;
			if(this.wssUrl === null) {
				reject(new Error('Missing Location Data'));
				return;
			}
			this.websocket = new WebSocket(this.wssUrl);

			this.websocket.onopen = () => {
				console.log('[open] Connection established');
				this.clearAndStartWebsocketHeartbeat();
				// Reset the reconnect count to 0 after a successful connection
				this.websocketReconnectCount = 0;
				resolve('done');
			};

			this.websocket.onmessage = async (event) => {
				// If the message is just DUB (it has a trailing newline it looks like),
				// then mark heartbeat as good and restart checks
				if(event.data.indexOf('DUB') === 0) {
					if(this.websocketDownTimeout !== null) {
						clearTimeout(this.websocketDownTimeout);
						this.clearAndStartWebsocketHeartbeat();
						return; // Bail
					}
				}
				this.clearAndStartWebsocketHeartbeat();

				const start = event.data.indexOf(':') as number;
				const end = event.data.indexOf('{');
				const ack = `ACK:${event.data.substring(start + 1, end - 1)}`; // +1 for ":", -1 for trailing space
				this.websocket?.send(ack);
				const payload = JSON.parse(event.data.substring(end));

				if(payload.notify === 'notify_push_order_items_status') {
					const lineItemStatus = Number(payload.object.line_item_status) as TLineItemStatus;
					if(TicketsStore.activeTicket) {
						if(TicketsStore.activeTicketLineItemsAggregateStatus < lineItemStatus) {
							let closedDate = TicketsStore.activeTicket.closedDate;
							// We need to set this so that latest ticket has a closed date without
							// needing a refresh
							if(lineItemStatus >= LineItemStatus.DELIVERED) {
								closedDate = dayjs().unix();
							}
							TicketsStore.addTicket({
								...TicketsStore.activeTicket,
								lineItemsAggregateStatus: lineItemStatus,
								modifiedDate: dayjs().unix(),
								closedDate: closedDate
							});
							await this.postTicketPushLogic();
						}
					} else {
						// Close the socket, we don't have an order
						this.stopWebsockets();
					}
				} else if(payload.notify === 'notify_push_order_cancelled') {
					// For now, we can assume this is for the active ticket. If we support multiple
					// tickets in the future we will need to look at the `order_id` and convert it to a
					// ticket id to remove the correct ticket
					if(TicketsStore.activeTicket) {
						TicketsStore.addTicket({
							...TicketsStore.activeTicket,
							lineItemsAggregateStatus: LineItemStatus.CANCELLED,
							status: TICKET_STATUS.CANCELLED,
							modifiedDate: dayjs().unix()
						});
						await this.postTicketPushLogic();
					} else {
						// Close the socket, we don't have an order
						this.stopWebsockets();
					}
				} else {
					console.log('Unknown WS Payload', payload);
				}
			};

			this.websocket.onclose = (event) => {
				console.log('CLOSE', event);
				if(event.wasClean) {
					console.log(`[close] Connection closed cleanly, code=${event.code} reason=${event.reason}`);
				} else if(!this.ignoreDirtyClose) {
					// e.g. server process killed or network down
					// event.code is usually 1006 in this case
					console.log('[close] Connection died', event);
					this.restartWebsockets();
				}
			};

			this.websocket.onerror = (error: any) => {
				console.log(`[error] ${error.message}`);
				reject(new Error(error.message));
			};
		});
	}

	async postTicketPushLogic() {
		if(TicketsStore.activeTicketLineItemsAggregateStatus >= LineItemStatus.DELIVERED) {
			// If the order is ready, we can stop the websocket
			this.stopWebsockets();
			if(TicketsStore.activeTicketLineItemsAggregateStatus !== LineItemStatus.CANCELLED) {
				await SoundService.playOrderReadySound();
			}

			// Order should now be available in recents
			RecentItemStore.fetchRecentItems().then();
			CustomerProfileStore.fetchProfile().then();

			if(TicketsStore.activeTicketLineItemsAggregateStatus !== LineItemStatus.CANCELLED) {
				// show Thank You page after delay for Generic apps.
				if(SessionStore.isBrand(BRAND.GENERIC)) {
					setTimeout(() => {
						DependencyService.getRouter().push({
							name: ERouteName.ThankYou,
							params: {
								type: 'pickup',
							},
						}).then();
					}, Math.PI * 1000);
				}
			}
		}
	}

	restartWebsockets() {
		this.stopWebsockets();
		const retryDelay = Math.pow(2, this.websocketReconnectCount) * 1000;
		console.log(`Restarting websocket connection in ${retryDelay}ms`);
		setTimeout(() => {
			console.log('Restarting Websockets');
			this.startWebsockets();
		}, retryDelay);
	}

	clearAndStartWebsocketHeartbeat() {
		if(this.websocketHeartbeatTimeout !== null) {
			clearTimeout(this.websocketHeartbeatTimeout);
		}
		this.websocketHeartbeatTimeout = setTimeout(() => {
			this.doWebsocketHeartbeat();
		}, this.websocketHeartbeatInterval) as unknown as number;
	}

	doWebsocketHeartbeat() {
		if(this.websocket) {
			// Wait up to 2 second to decide the connection is stale
			this.websocketDownTimeout = setTimeout(() => {
				console.log('Didn\'t get DUB, Restarting API');
				this.restartWebsockets();
			}, this.websocketDownInterval) as unknown as number;
			this.websocket.send('LUB');
		}
	}

	/**
	 * determines if image is bundled with app based on path
	 */
	isBundledImage(imagePath: string) {
		return imagePath.startsWith('/images/');
	}
}

export const WebService = new Web();
